import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { boot } from 'quasar/wrappers';
import { useAuthStore } from 'stores/auth-store';
import { isString } from 'src/utils/isString';
import { notifyError, notifySuccess } from 'src/utils';

interface ApiErrorResponse {
  message?: string;
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Skip the generic error toast for this request, for callers that render the
     * failure themselves.
     *
     * Only suppresses the notifications built from the API's own `message` — a
     * caller showing that message inline would otherwise show it twice. The
     * session-level handling of 401, 403 and 503 is unaffected: those are not
     * this caller's business to swallow.
     */
    suppressErrorNotify?: boolean;
  }
}

function suppressesErrorNotify(config: unknown): boolean {
  return (
    (config as AxiosRequestConfig | undefined)?.suppressErrorNotify === true
  );
}

interface ApiSuccessResponse {
  message?: string;
  [key: string]: unknown;
}

const api = axios.create({
  baseURL: process.env.API_URL,
  withCredentials: true,
  timeout: 30000,
});

api.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
api.defaults.headers.common['Content-Type'] = 'application/vnd.api+json';

const mappedErrorMessages: Record<string, string> = {
  ERROR: 'An error occurred, please try again later.',
};

export default boot(({ app, router }) => {
  api.interceptors.response.use(
    function (response: AxiosResponse<ApiSuccessResponse>) {
      // Check for 422 validation errors
      if (response.status === 422 && isString(response?.data?.message)) {
        if (!suppressesErrorNotify(response.config)) {
          notifyError(response.data.message);
        }
        return Promise.reject(response);
      }

      // General Notifier if 'message' in payload for successful responses
      if (isString(response?.data?.message)) {
        notifySuccess(response.data.message);
      }

      return response;
    },
    function (error: AxiosError<ApiErrorResponse>) {
      const status = error?.response?.status;

      // Handle Maintenance Mode Redirect
      if (status === 503) {
        const authStore = useAuthStore();
        authStore.forceLogout();

        notifyError(
          'The system is currently down for maintenance. Please try again later.'
        );

        router.push('/auth/maintenance');
        return Promise.reject(error);
      }

      // Handle Unauthorized
      if (status === 401) {
        const authStore = useAuthStore();
        const currentPath = router.currentRoute.value.path;
        const isOnAuthPage = currentPath.startsWith('/auth');

        authStore.forceLogout();

        if (!isOnAuthPage) {
          notifyError(
            'You have been logged out due to inactivity, please log in again.'
          );
        }

        return Promise.reject(error);
      }

      // Handle Forbidden (403) - user lacks permission
      if (status === 403) {
        notifyError('Unfortunately, you do not have access to this item.');
        return Promise.reject(error);
      }

      const errorMessage = error.response?.data?.message || error.message || '';
      const message: string = mappedErrorMessages[errorMessage] || errorMessage;

      // Suppress generic status code messages
      if (
        !message.includes('status code') &&
        !suppressesErrorNotify(error.config)
      ) {
        notifyError(message);
      }

      if (mappedErrorMessages[errorMessage]) {
        return Promise.resolve(error);
      }

      return Promise.reject(error);
    }
  );

  app.config.globalProperties.$axios = axios;
  app.config.globalProperties.$api = api;
});

export { axios, api };
